import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaPlus, FaTimes, FaLock, FaLockOpen,
  FaEdit, FaTrash, FaDownload, FaFilePdf, FaFileExcel, FaEye, FaFileImport
} from 'react-icons/fa';
import './css_pages/PhysicalInventory.css';
import {
  getAllPhysicalInventories, getPhysicalInventoryById,
  getProductsForPhysicalInventory, createPhysicalInventory,
  updatePhysicalInventory, toggleLockPhysicalInventory, deletePhysicalInventory
} from '../services/physicalinventoryService';
import { useStore } from '../contexts/StoreContext';

// ── Date helpers ──────────────────────────────────────────────
const formatDateCA = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { month: 'long', day: '2-digit', year: 'numeric' });
};
const formatDateTimeCA = (dateStr) => {
  if (!dateStr) return '—';
  const d    = new Date(dateStr);
  const date = d.toLocaleDateString('en-CA', { month: 'long', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date}, ${time}`;
};

// ── Grouping helpers ──────────────────────────────────────────
const groupByCategory = (items) =>
  items.reduce((acc, item) => {
    const cat = item.category_name || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

const groupByTypeAndCategory = (items) =>
  items.reduce((acc, item) => {
    const type = item.product_type_name || 'Other';
    const cat  = item.category_name     || 'Uncategorized';
    if (!acc[type])      acc[type]      = {};
    if (!acc[type][cat]) acc[type][cat] = [];
    acc[type][cat].push(item);
    return acc;
  }, {});

const getCountByItem = (item) => {
  if (item.item_type === 'prep') return item.container_unit || '—';
  return item.count_by || item.container_type || '—';
};

// ── Export helpers ────────────────────────────────────────────
const exportToCSV = (items, storeName, date, groupBy = 'category') => {
  const headers = ['Group','Category','Product Code','Product Name','Last Inv','Purchase','Count By','INV','INV 2','INV 3'];
  const rows = [];
  if (groupBy === 'type') {
    const grouped = groupByTypeAndCategory(items);
    Object.entries(grouped).forEach(([typeName, categories]) => {
      rows.push([typeName,'','','','','','','','','']);
      Object.entries(categories).forEach(([catName, catItems]) => {
        rows.push(['',catName,'','','','','','','','']);
        catItems.forEach(item => rows.push(['','',item.product_code||'',item.product_name||'',item.last_inv_quantity??0,item.purchase??0,getCountByItem(item),'','','']));
      });
    });
  } else {
    const grouped = groupByCategory(items);
    Object.entries(grouped).forEach(([catName, catItems]) => {
      rows.push([catName,'','','','','','','','','']);
      catItems.forEach(item => rows.push(['','',item.product_code||'',item.product_name||'',item.last_inv_quantity??0,item.purchase??0,getCountByItem(item),'','','']));
    });
  }
  const csv  = [headers,...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a    = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `Physical_Inventory_${storeName}_${date}.csv` });
  a.click(); URL.revokeObjectURL(a.href);
};

const exportToPDF = (items, storeName, date, groupBy = 'category') => {
  const win = window.open('', '_blank');
  let rows = '';
  const rowHtml = p => `<tr><td>${p.product_code||'-'}</td><td>${p.product_name}</td><td>${p.last_inv_quantity??0}</td><td>${p.purchase??0}</td><td>${getCountByItem(p)}</td><td></td><td></td><td></td></tr>`;
  if (groupBy === 'type') {
    const g = groupByTypeAndCategory(items);
    rows = Object.entries(g).map(([t, cats]) => `<tr class="type-row"><td colspan="8"><strong>${t}</strong></td></tr>${Object.entries(cats).map(([c, ci]) => `<tr class="cat-row"><td colspan="8">&nbsp;&nbsp;&nbsp;${c}</td></tr>${ci.map(rowHtml).join('')}`).join('')}`).join('');
  } else {
    const g = groupByCategory(items);
    rows = Object.entries(g).map(([c, ci]) => `<tr class="cat-row"><td colspan="8"><strong>${c}</strong></td></tr>${ci.map(rowHtml).join('')}`).join('');
  }
  win.document.write(`<!DOCTYPE html><html><head><title>Physical Inventory - ${storeName}</title><style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h1{font-size:18px}table{width:100%;border-collapse:collapse}th{background:#0ea5e9;color:white;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}.type-row td{background:#1e293b;color:#fff;font-weight:bold}.cat-row td{background:#f1f5f9;font-weight:600}@media print{button{display:none}}</style></head><body><h1>Physical Inventory — ${storeName}</h1><p>Date: ${date} | Grouped by: ${groupBy==='type'?'Product Type → Category':'Category'}</p><button onclick="window.print()" style="margin-bottom:16px;padding:8px 16px;background:#0ea5e9;color:white;border:none;border-radius:4px;cursor:pointer;">Print / Save PDF</button><table><thead><tr><th>Product Code</th><th>Product Name</th><th>Last Inv</th><th>Purchase</th><th>Count By</th><th>INV</th><th>INV 2</th><th>INV 3</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
  win.document.close();
};

const exportToImport = (items, storeName, date) => {
  const headers = ['Product Name','Product Code','Last Inv','Purchase','Quantity','Weight Type'];
  const rows = [...items].sort((a,b) => (a.product_name||'').localeCompare(b.product_name||'')).map(item => [item.product_name||'',item.product_code||'',item.last_inv_quantity??0,item.purchase??0,'',getCountByItem(item)]);
  const csv  = [headers,...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a    = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `Import_Template_${storeName}_${date}.csv` });
  a.click(); URL.revokeObjectURL(a.href);
};

// ════════════════════════════════════════════════════════════════
function PhysicalInventory() {
  const [inventories, setInventories]         = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [showModal, setShowModal]             = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal]     = useState(false);
  const [viewInventory, setViewInventory]     = useState(null);
  const [editingId, setEditingId]             = useState(null);
  const [deleteId, setDeleteId]               = useState(null);
  const [exportLoading, setExportLoading]     = useState(false);
  const [importLoading, setImportLoading]     = useState(false);
  const [showHeaderDownloadMenu, setShowHeaderDownloadMenu] = useState(false);
  const [selectedInvForExport, setSelectedInvForExport]     = useState(null);
  const [groupBy, setGroupBy] = useState('category');

  const headerDownloadRef = useRef(null);
  const { selectedStore }  = useStore();

  const getCurrentLocalDateTime = () => {
    const now = new Date(), pad = n => String(n).padStart(2,'0');
    return `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const [formData, setFormData] = useState({ inventoryDate: getCurrentLocalDateTime(), items: [] });

  // ── Fetch ──
  const fetchInventories = useCallback(async () => {
    if (!selectedStore) return;
    try {
      setLoading(true); setError(null);
      const r = await getAllPhysicalInventories(selectedStore.id);
      if (r.success) { setInventories(r.data); if (r.data.length > 0) setSelectedInvForExport(r.data[0]); }
    } catch { setError('Error loading physical inventories'); }
    finally  { setLoading(false); }
  }, [selectedStore]);

  useEffect(() => { fetchInventories(); }, [fetchInventories]);

  useEffect(() => {
    const handler = (e) => { if (headerDownloadRef.current && !headerDownloadRef.current.contains(e.target)) setShowHeaderDownloadMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showModal || showDeleteModal || showViewModal) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [showModal, showDeleteModal, showViewModal]);

  // ── View ──
  const handleView = async (id) => {
    try {
      setLoading(true);
      const r = await getPhysicalInventoryById(id);
      if (r.success) { setViewInventory(r.data); setShowViewModal(true); }
    } catch { alert('Error loading inventory'); }
    finally  { setLoading(false); }
  };

  // ── Create ──
  const handleOpenCreateModal = async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      const r = await getProductsForPhysicalInventory(selectedStore.id);
      if (r.success) {
        setFormData({
          inventoryDate: getCurrentLocalDateTime(),
          items: r.data.map((p, i) => ({
            id_products: p.id_products, id_preps: p.id_preps || null,
            item_type: p.item_type || 'product', product_name: p.product_name,
            product_code: p.product_code, category_name: p.category_name,
            product_type_name: p.product_type_name || 'Other',
            container_size: p.container_size || null, container_unit: p.container_unit || '',
            container_type: p.container_type || '', count_by: p.count_by || '',
            wholesale_price: p.wholesale_price || 0, case_size: p.case_size || null,
            last_inv_quantity: p.last_inv_quantity || 0,
            purchase: p.purchase ?? 0, inv_quantity: '', display_order: i + 1
          }))
        });
        setEditingId(null); setShowModal(true);
      }
    } catch { alert('Error loading products'); }
    finally  { setLoading(false); }
  };

  // ── Edit ──
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const r = await getPhysicalInventoryById(id);
      if (r.success) {
        const inv = r.data;
        if (inv.status === 'Locked') { alert('Cannot edit a locked inventory'); return; }
        const d = new Date(inv.inventory_date), pad = n => String(n).padStart(2,'0');
        setFormData({
          inventoryDate: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
          items: inv.items.map((item, i) => ({
            id_products: item.id_product, id_preps: item.id_preps || null,
            item_type: item.item_type || 'product', product_name: item.product_name,
            product_code: item.product_code, category_name: item.category_name,
            product_type_name: item.product_type_name || 'Other',
            container_size: item.container_size || null, container_unit: item.container_unit || '',
            container_type: item.container_type || '', count_by: item.count_by || '',
            wholesale_price: item.wholesale_price || 0, case_size: item.case_size || null,
            last_inv_quantity: item.last_inv_quantity || 0,
            purchase: item.purchase ?? 0, inv_quantity: item.inv_quantity ?? '',
            display_order: item.display_order || i + 1
          }))
        });
        setEditingId(id); setShowModal(true);
      }
    } catch { alert('Error loading inventory'); }
    finally  { setLoading(false); }
  };

  const handleCloseModal = () => { setShowModal(false); setEditingId(null); setFormData({ inventoryDate: getCurrentLocalDateTime(), items: [] }); };
  const handleInvChange  = (index, value) => { const updated = [...formData.items]; updated[index] = { ...updated[index], inv_quantity: value }; setFormData({ ...formData, items: updated }); };

  const handleSave = async () => {
    if (!selectedStore) return;
    try {
      setLoading(true);
      const payload = { storeId: selectedStore.id, inventoryDate: formData.inventoryDate, status: 'Unlocked', items: formData.items };
      const r = editingId ? await updatePhysicalInventory(editingId, payload) : await createPhysicalInventory(payload);
      if (r.success) { if (!editingId && r.data?.id) setEditingId(r.data.id); alert('Saved successfully'); await fetchInventories(); }
    } catch (err) { alert('Error saving: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handleSaveAndClose = async () => {
    if (!selectedStore) return;
    if (!formData.items.some(item => item.inv_quantity !== '' && item.inv_quantity !== null)) { alert('Please enter at least one quantity'); return; }
    try {
      setLoading(true);
      const payload = { storeId: selectedStore.id, inventoryDate: formData.inventoryDate, status: 'Locked', items: formData.items };
      const r = editingId ? await updatePhysicalInventory(editingId, payload) : await createPhysicalInventory(payload);
      if (r.success) { alert('Physical inventory closed successfully'); await fetchInventories(); handleCloseModal(); }
    } catch (err) { alert('Error saving: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handleToggleLock = async (id) => {
    try { setLoading(true); const r = await toggleLockPhysicalInventory(id); if (r.success) await fetchInventories(); }
    catch { alert('Error updating status'); }
    finally { setLoading(false); }
  };

  const handleOpenDeleteModal  = (id) => { setDeleteId(id); setShowDeleteModal(true); };
  const handleCloseDeleteModal = () => { setDeleteId(null); setShowDeleteModal(false); };
  const handleConfirmDelete    = async () => {
    try { setLoading(true); const r = await deletePhysicalInventory(deleteId); if (r.success) await fetchInventories(); handleCloseDeleteModal(); }
    catch (err) { alert('Error deleting: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  // ── Exports ──
  const handleHeaderExportPDF = async () => {
    setShowHeaderDownloadMenu(false);
    try {
      setExportLoading(true);
      const r = await getProductsForPhysicalInventory(selectedStore.id);
      if (r.success) exportToPDF(r.data, selectedStore?.name||'Store', selectedInvForExport ? formatDateCA(selectedInvForExport.inventory_date) : formatDateCA(new Date().toISOString()), groupBy);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setExportLoading(false); }
  };
  const handleHeaderExportCSV = async () => {
    setShowHeaderDownloadMenu(false);
    try {
      setExportLoading(true);
      const r = await getProductsForPhysicalInventory(selectedStore.id);
      if (r.success) exportToCSV(r.data, selectedStore?.name||'Store', selectedInvForExport ? formatDateCA(selectedInvForExport.inventory_date) : formatDateCA(new Date().toISOString()), groupBy);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setExportLoading(false); }
  };
  const handleExportToImport = async () => {
    try {
      setImportLoading(true);
      const r = await getProductsForPhysicalInventory(selectedStore.id);
      if (r.success) exportToImport(r.data, selectedStore?.name||'Store', selectedInvForExport ? formatDateCA(selectedInvForExport.inventory_date) : formatDateCA(new Date().toISOString()));
    } catch (err) { alert('Error: ' + err.message); }
    finally { setImportLoading(false); }
  };

  // ── GroupBy Toggle ──
  const GroupByToggle = () => (
    <div className="pi-groupby-toggle">
      <span className="pi-groupby-label">Group by:</span>
      <button className={`pi-toggle-btn ${groupBy === 'category' ? 'active' : ''}`} onClick={() => setGroupBy('category')} type="button">Product Type</button>
      <button className={`pi-toggle-btn ${groupBy === 'type'     ? 'active' : ''}`} onClick={() => setGroupBy('type')}     type="button">Category</button>
    </div>
  );

  // ── Render rows ──
  const renderRows = (items, withInput = false) => {
    const colSpan = 7;
    const Row = (item) => {
      const idx = withInput
        ? items.findIndex(i => item.item_type === 'prep' ? i.id_preps === item.id_preps : i.id_products === item.id_products)
        : -1;
      return (
        <tr key={`${item.id_products ?? item.id_preps}`} className="pi-product-row">
          <td className="pi-cell-category">—</td>
          <td className="pi-cell-code">{item.product_code || '-'}</td>
          <td className="pi-cell-name">{item.product_name}</td>
          <td className="pi-cell-lastinv">{item.last_inv_quantity ?? 0}</td>
          <td className="pi-cell-purchase">{item.purchase !== undefined ? Number(item.purchase).toFixed(2) : '—'}</td>
          <td className="pi-cell-countby">{getCountByItem(item)}</td>
          <td className="pi-cell-inv">
            {withInput
              ? <input type="number" step="0.01" min="0" value={item.inv_quantity ?? ''} onChange={e => handleInvChange(idx, e.target.value)} placeholder="0" disabled={loading} className="pi-input" />
              : <strong>{item.inv_quantity ?? '—'}</strong>}
          </td>
        </tr>
      );
    };

    if (groupBy === 'type') {
      const grouped = groupByTypeAndCategory(items);
      return Object.entries(grouped).map(([typeName, categories]) => (
        <React.Fragment key={typeName}>
          <tr className="pi-type-row"><td colSpan={colSpan}>{typeName}</td></tr>
          {Object.entries(categories).map(([catName, catItems]) => (
            <React.Fragment key={`${typeName}-${catName}`}>
              <tr className="pi-category-row pi-category-sub"><td colSpan={colSpan}>&nbsp;&nbsp;&nbsp;{catName}</td></tr>
              {catItems.map(Row)}
            </React.Fragment>
          ))}
        </React.Fragment>
      ));
    }
    const grouped = groupByCategory(items);
    return Object.entries(grouped).map(([catName, catItems]) => (
      <React.Fragment key={catName}>
        <tr className="pi-category-row"><td colSpan={colSpan}>{catName}</td></tr>
        {catItems.map(Row)}
      </React.Fragment>
    ));
  };

  const renderTable = () => (
    <div className="pi-table-container">
      <table className="pi-table">
        <thead><tr><th>Category</th><th>Product Code</th><th>Product Name</th><th>Last Inv</th><th>Purchase</th><th>Count By</th><th>INV</th></tr></thead>
        <tbody>{renderRows(formData.items, true)}</tbody>
      </table>
    </div>
  );

  const renderMobileCards = () => {
    const Card = (item) => {
      const idx = formData.items.findIndex(i => item.item_type === 'prep' ? i.id_preps === item.id_preps : i.id_products === item.id_products);
      return (
        <div key={`card-${item.id_products ?? item.id_preps}`} className="pi-mobile-product-card">
          <div className="pi-mobile-product-name">{item.product_name}</div>
          <div className="pi-mobile-product-code">{item.product_code || '-'}</div>
          <div className="pi-mobile-fields">
            <div className="pi-mobile-field"><span className="pi-mobile-label">Last Inv</span><span className="pi-mobile-value">{item.last_inv_quantity ?? 0}</span></div>
            <div className="pi-mobile-field"><span className="pi-mobile-label">Purchase</span><span className="pi-mobile-value">{item.purchase !== undefined ? Number(item.purchase).toFixed(2) : '—'}</span></div>
            <div className="pi-mobile-field"><span className="pi-mobile-label">Count By</span><span className="pi-mobile-value">{getCountByItem(item)}</span></div>
            <div className="pi-mobile-field">
              <span className="pi-mobile-label">INV *</span>
              <input type="number" step="0.01" min="0" value={item.inv_quantity ?? ''} onChange={e => handleInvChange(idx, e.target.value)} placeholder="0" disabled={loading} className="pi-mobile-input" />
            </div>
          </div>
        </div>
      );
    };
    if (groupBy === 'type') {
      const grouped = groupByTypeAndCategory(formData.items);
      return (
        <div className="pi-mobile-cards">
          {Object.entries(grouped).map(([typeName, categories]) => (
            <div key={typeName} className="pi-mobile-type-group">
              <div className="pi-mobile-type-header">{typeName}</div>
              {Object.entries(categories).map(([catName, catItems]) => (
                <div key={`${typeName}-${catName}`} className="pi-mobile-category">
                  <div className="pi-mobile-category-header pi-mobile-sub-header">{catName}</div>
                  {catItems.map(Card)}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    const grouped = groupByCategory(formData.items);
    return (
      <div className="pi-mobile-cards">
        {Object.entries(grouped).map(([catName, catItems]) => (
          <div key={catName} className="pi-mobile-category">
            <div className="pi-mobile-category-header">{catName}</div>
            {catItems.map(Card)}
          </div>
        ))}
      </div>
    );
  };

  if (!selectedStore) {
    return (
      <div className="pi-page">
        <div className="pi-page-header"><h1 className="pi-page-title">Physical Inventory</h1></div>
        <div className="pi-no-store-msg"><p>Please select a store from the sidebar</p></div>
      </div>
    );
  }

  return (
    <div className="pi-page">

      {/* ── HEADER ── */}
      <div className="pi-page-header pi-sticky-header">
        <div className="pi-header-left">
          <h1 className="pi-page-title">Physical Inventory</h1>
          <span className="pi-store-badge">{selectedStore.name}</span>
        </div>
        <div className="pi-header-buttons">
          <GroupByToggle />
          <button className="pi-btn-import-export" onClick={handleExportToImport} disabled={importLoading} title="Download import template">
            <FaFileImport /> {importLoading ? 'Exporting...' : 'To Import'}
          </button>
          <div className="pi-download-wrapper" ref={headerDownloadRef}>
            <button className="pi-btn-download" onClick={() => setShowHeaderDownloadMenu(!showHeaderDownloadMenu)} disabled={exportLoading}>
              <FaDownload /> {exportLoading ? 'Exporting...' : 'Print Format'}
            </button>
            {showHeaderDownloadMenu && (
              <div className="pi-download-menu">
                <button className="pi-download-option" onClick={handleHeaderExportPDF}><FaFilePdf /> PDF / Print</button>
                <button className="pi-download-option" onClick={handleHeaderExportCSV}><FaFileExcel /> Excel (CSV)</button>
              </div>
            )}
          </div>
          <button className="pi-btn-primary" onClick={handleOpenCreateModal} disabled={loading}>
            <FaPlus /> Create new
          </button>
        </div>
      </div>

      {error && (
        <div className="pi-error-msg">
          {error}
          <button onClick={fetchInventories}>Retry</button>
        </div>
      )}

      {/* ── MAIN TABLE ── */}
      <div className="pi-list-table-wrap">
        {loading && <div className="pi-loading">Loading…</div>}
        <table className="pi-list-table">
          <thead>
            <tr><th>Date</th><th>No. Products</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {inventories.length === 0 ? (
              <tr><td colSpan="4" style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>{loading ? 'Loading…' : 'No physical inventories found'}</td></tr>
            ) : inventories.map(inv => (
              <tr key={inv.id_inventories}>
                <td>{formatDateTimeCA(inv.inventory_date)}</td>
                <td>{inv.total_products}</td>
                <td>
                  <span className={`pi-status-badge ${inv.status.toLowerCase()}`}>
                    {inv.status === 'Locked' ? <FaLock /> : <FaLockOpen />} {inv.status}
                  </span>
                </td>
                <td>
                  <div className="pi-action-btns">
                    <button className="pi-btn-action pi-lock" onClick={() => handleToggleLock(inv.id_inventories)} disabled={loading} title={inv.status === 'Locked' ? 'Unlock' : 'Lock'}>
                      {inv.status === 'Locked' ? <FaLockOpen /> : <FaLock />}
                    </button>
                    {inv.status === 'Locked' && (
                      <button className="pi-btn-action" onClick={() => handleView(inv.id_inventories)} disabled={loading} title="View"><FaEye /></button>
                    )}
                    {inv.status === 'Unlocked' && (
                      <>
                        <button className="pi-btn-action" onClick={() => handleEdit(inv.id_inventories)} disabled={loading} title="Edit"><FaEdit /></button>
                        <button className="pi-btn-action pi-delete" onClick={() => handleOpenDeleteModal(inv.id_inventories)} disabled={loading} title="Delete"><FaTrash /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── MODAL VIEW ── */}
      {showViewModal && viewInventory && (
        <div className="pi-modal-overlay">
          <div className="pi-modal pi-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="pi-modal-header">
              <h2>Physical Inventory — {formatDateTimeCA(viewInventory.inventory_date)}</h2>
              <button className="pi-btn-close" onClick={() => setShowViewModal(false)}><FaTimes /></button>
            </div>
            {/* Fields bar */}
            <div className="pi-modal-fields-bar">
              <GroupByToggle />
            </div>
            {/* Scrollable body */}
            <div className="pi-modal-body-scroll">
              <div className="pi-table-container">
                <table className="pi-table">
                  <thead><tr><th>Category</th><th>Product Code</th><th>Product Name</th><th>Last Inv</th><th>Purchase</th><th>Count By</th><th>INV</th></tr></thead>
                  <tbody>{renderRows(viewInventory.items || [], false)}</tbody>
                </table>
              </div>
            </div>
            <div className="pi-modal-footer">
              <div className="pi-footer-totals">
                <div className="pi-total-item"><span className="label">Products:</span><span className="value">{viewInventory.items?.length || 0}</span></div>
              </div>
              <div className="pi-footer-actions">
                <button className="pi-btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CREATE / EDIT ── */}
      {showModal && (
        <div className="pi-modal-overlay">
          <div className="pi-modal pi-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="pi-modal-header">
              <h2>{editingId ? 'Edit Physical Inventory' : 'New Physical Inventory'}</h2>
              <button className="pi-btn-close" onClick={handleCloseModal}><FaTimes /></button>
            </div>
            {/* Fields bar — date + group by */}
            <div className="pi-modal-fields-bar">
              <div className="pi-form-group">
                <label>Date *</label>
                <input type="datetime-local" value={formData.inventoryDate}
                  onChange={e => setFormData({ ...formData, inventoryDate: e.target.value })}
                  disabled={loading} />
              </div>
              <GroupByToggle />
            </div>
            {/* Scrollable body — product table */}
            <div className="pi-modal-body-scroll">
              <div className="pi-desktop-table">
                {formData.items.length === 0
                  ? <div className="pi-loading">Loading products…</div>
                  : renderTable()}
              </div>
              <div className="pi-mobile-only">
                {formData.items.length === 0
                  ? <div className="pi-loading">Loading products…</div>
                  : renderMobileCards()}
              </div>
            </div>
            <div className="pi-modal-footer">
              <div className="pi-footer-totals">
                <div className="pi-total-item">
                  <span className="label">Products:</span>
                  <span className="value">{formData.items.length}</span>
                </div>
              </div>
              <div className="pi-footer-actions">
                <button className="pi-btn-secondary"  onClick={handleCloseModal}   disabled={loading}>Cancel</button>
                <button className="pi-btn-save-draft" onClick={handleSave}         disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
                <button className="pi-btn-primary"    onClick={handleSaveAndClose} disabled={loading}>{loading ? 'Saving…' : 'Save & Close'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DELETE ── */}
      {showDeleteModal && (
        <div className="pi-modal-overlay">
          <div className="pi-modal pi-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="pi-modal-header">
              <h2>Confirm deletion</h2>
              <button className="pi-btn-close" onClick={handleCloseDeleteModal}><FaTimes /></button>
            </div>
            <div className="pi-modal-body">
              <p style={{ fontSize:14, color:'#374151', marginBottom:10 }}>Are you sure you want to delete this physical inventory?</p>
              <p className="pi-warning-text">This action cannot be undone.</p>
            </div>
            <div className="pi-modal-footer">
              <div className="pi-footer-totals" />
              <div className="pi-footer-actions">
                <button className="pi-btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>Cancel</button>
                <button className="pi-btn-danger"    onClick={handleConfirmDelete}    disabled={loading}>{loading ? 'Deleting…' : 'Delete'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default PhysicalInventory;